/**
 * Wishlist Store — Persistent favorite products with Supabase Cloud DB & localStorage sync.
 * Integrates with ProductCard, Product Detail, and Account Dashboard.
 */

import { useState, useEffect } from "react";
import type { Product } from "@/lib/products";
import { supabase } from "@/lib/supabase";

const WISHLIST_KEY = "verdant_user_wishlist";

export function getStoredWishlist(): Product[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    return raw ? (JSON.parse(raw) as Product[]) : [];
  } catch {
    return [];
  }
}

export function saveWishlist(items: Product[]): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  } catch (err) {
    console.error("Failed to save wishlist locally", err);
  }
}

export function useWishlist() {
  const [wishlist, setWishlist] = useState<Product[]>([]);

  useEffect(() => {
    setWishlist(getStoredWishlist());
  }, []);

  const toggleWishlist = (product: Product) => {
    const exists = wishlist.some((p) => p.id === product.id);
    let updated: Product[];
    if (exists) {
      updated = wishlist.filter((p) => p.id !== product.id);

      // Async removal from Supabase
      supabase.auth.getUser().then(({ data }) => {
        if (data?.user) {
          supabase
            .from("wishlists")
            .delete()
            .eq("user_id", data.user.id)
            .eq("product_id", product.id)
            .then(() => {});
        }
      });
    } else {
      updated = [...wishlist, product];

      // Async insertion to Supabase
      supabase.auth.getUser().then(({ data }) => {
        if (data?.user) {
          supabase
            .from("wishlists")
            .insert({ user_id: data.user.id, product_id: product.id })
            .then(() => {});
        }
      });
    }

    setWishlist(updated);
    saveWishlist(updated);
  };

  const isInWishlist = (productId: string) => {
    return wishlist.some((p) => p.id === productId);
  };

  const removeWishlist = (productId: string) => {
    const updated = wishlist.filter((p) => p.id !== productId);
    setWishlist(updated);
    saveWishlist(updated);

    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) {
        supabase
          .from("wishlists")
          .delete()
          .eq("user_id", data.user.id)
          .eq("product_id", productId)
          .then(() => {});
      }
    });
  };

  return {
    wishlist,
    count: wishlist.length,
    toggleWishlist,
    isInWishlist,
    removeWishlist,
  };
}
